import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminRoute from "@/components/AdminRoute";
import AgencyAdminRoute from "@/components/AgencyAdminRoute";
import Login from "@/pages/Login";
import SignAddendum from "@/pages/SignAddendum";
import Register from "@/pages/Register";
import RequestAccess from "@/pages/RequestAccess";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import NewProposal from "@/pages/NewProposal";
import ProposalEdit from "@/pages/ProposalEdit";
import ProposalView from "@/pages/ProposalView";
import Proposals from "@/pages/Proposals";
import Settings from "@/pages/Settings";
import MyProfile from "@/pages/MyProfile";
import CrmContacts from "@/pages/CrmContacts";
import CrmDealers from "@/pages/CrmDealers";
import ProformaList from "@/pages/ProformaList";
import ProformaEdit from "@/pages/ProformaEdit";
import DealersList from "@/pages/DealersList";
import DealerDetail, { DealerSignPage } from "@/pages/DealerDetail";
import DealerInfoPage from "@/pages/DealerInfoPage";
import AdminDealerAgreement from "@/pages/AdminDealerAgreement";
import AdminDealerForms from "@/pages/AdminDealerForms";
import AdminUsers from "@/pages/AdminUsers";
import AdminAgencies from "@/pages/AdminAgencies";
import AgencyUsers from "@/pages/AgencyUsers";
import AdminProposals from "@/pages/AdminProposals";
import AdminRates from "@/pages/AdminRates";
import AdminProductionRateCards from "@/pages/AdminProductionRateCards";
import AdminSSO from "@/pages/AdminSSO";
import PortfolioVertical from "@/pages/PortfolioVertical";
import CommissionsFrame from "@/pages/CommissionsFrame";
import MarketingCatalog from "@/pages/MarketingCatalog";
import MarketingMyOrders from "@/pages/MarketingMyOrders";
import EmailCampaigns from "@/pages/EmailCampaigns";
import AdminMarketing from "@/pages/AdminMarketing";
import PublicRateCard from "@/pages/PublicRateCard";
import OnboardingBoard from "@/pages/OnboardingBoard";
import Tasks from "@/pages/Tasks";
import CalendarPage from "@/pages/CalendarPage";
import DealerProfile from "@/pages/DealerProfile";
import AddExistingDealer from "@/pages/AddExistingDealer";
import OnboardingProgramSetup from "@/pages/OnboardingProgramSetup";
import OnboardingDetail from "@/pages/OnboardingDetail";
import AdminProducts from "@/pages/AdminProducts";
import OnboardingAudit from "@/pages/OnboardingAudit";
import AgentOnboardings from "@/pages/AgentOnboardings";
import ExistingDealers from "@/pages/ExistingDealers";
import AdminAgents from "@/pages/AdminAgents";
import AdminAgentDetail from "@/pages/AdminAgentDetail";
import AgentSignPage from "@/pages/AgentSignPage";
import AgentWelcomePage from "@/pages/AgentWelcomePage";
import AgentApply from "@/pages/AgentApply";
import DealerApply from "@/pages/DealerApply";
import DealerSignupWizard from "@/pages/DealerSignupWizard";
import AdminOnboardingPriority from "@/pages/AdminOnboardingPriority";
import { Toaster } from "@/components/ui/sonner";

function RootRedirect() {
  const { user, ready } = useAuth();
  if (!ready) return null;
  return <Navigate to={user ? "/dashboard" : "/login"} replace />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors closeButton />
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/request-access" element={<RequestAccess />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/proposals" element={<ProtectedRoute><Proposals /></ProtectedRoute>} />
            <Route path="/proposals/new" element={<ProtectedRoute><NewProposal /></ProtectedRoute>} />
            <Route path="/proposals/:id" element={<ProtectedRoute><ProposalEdit /></ProtectedRoute>} />
            <Route path="/proposals/:id/view" element={<ProtectedRoute><ProposalView shareMode={false} /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><MyProfile /></ProtectedRoute>} />
            <Route path="/crm/contacts" element={<ProtectedRoute><CrmContacts /></ProtectedRoute>} />
            <Route path="/crm/dealers" element={<ProtectedRoute><CrmDealers /></ProtectedRoute>} />
            {/* Legacy — Analytics is merged into the dashboard */}
            <Route path="/analytics" element={<Navigate to="/dashboard" replace />} />
            <Route path="/proforma" element={<ProtectedRoute><ProformaList /></ProtectedRoute>} />
            <Route path="/proforma/:id" element={<ProtectedRoute><ProformaEdit /></ProtectedRoute>} />
            <Route path="/dealers" element={<ProtectedRoute><DealersList /></ProtectedRoute>} />
            <Route path="/dealers/new" element={<ProtectedRoute><DealerSignupWizard /></ProtectedRoute>} />
            <Route path="/dealers/new/:id" element={<ProtectedRoute><DealerSignupWizard /></ProtectedRoute>} />
            <Route path="/dealers/existing" element={<ProtectedRoute><ExistingDealers /></ProtectedRoute>} />
            <Route path="/dealers/add-existing" element={<ProtectedRoute><AddExistingDealer /></ProtectedRoute>} />
            <Route path="/dealers/:id/profile" element={<ProtectedRoute><DealerProfile /></ProtectedRoute>} />
            <Route path="/dealers/:id" element={<ProtectedRoute><DealerDetail /></ProtectedRoute>} />
            <Route path="/sign-dealer/:token" element={<DealerSignPage />} />
            <Route path="/sign-addendum/:token" element={<SignAddendum />} />
            <Route path="/dealer-info/:token" element={<DealerInfoPage />} />
            {/* Portfolio verticals — single dynamic route */}
            <Route path="/portfolio/:slug" element={<ProtectedRoute><PortfolioVertical /></ProtectedRoute>} />
            {/* Commissions module — iframed from sibling dealercomm workspace */}
            <Route path="/commissions" element={<ProtectedRoute><CommissionsFrame /></ProtectedRoute>} />
            <Route path="/commissions/*" element={<ProtectedRoute><CommissionsFrame /></ProtectedRoute>} />
            {/* Marketing e-commerce (agents order hard-copy materials) */}
            <Route path="/marketing/materials" element={<ProtectedRoute><MarketingCatalog /></ProtectedRoute>} />
            <Route path="/marketing/my-orders" element={<ProtectedRoute><MarketingMyOrders /></ProtectedRoute>} />
            <Route path="/marketing/email-campaigns" element={<ProtectedRoute><EmailCampaigns /></ProtectedRoute>} />
            <Route path="/admin/marketing" element={<AdminRoute><AdminMarketing /></AdminRoute>} />
            <Route path="/admin" element={<AdminRoute><Navigate to="/admin/users" replace /></AdminRoute>} />
            <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
            <Route path="/admin/agencies" element={<AdminRoute><AdminAgencies /></AdminRoute>} />
            <Route path="/admin/agencies/:agencyId/users" element={<AdminRoute><AgencyUsers /></AdminRoute>} />
            <Route path="/agency/users" element={<AgencyAdminRoute><AgencyUsers /></AgencyAdminRoute>} />
            <Route path="/admin/proposals" element={<AdminRoute><AdminProposals /></AdminRoute>} />
            <Route path="/admin/rates" element={<AdminRoute><AdminRates /></AdminRoute>} />
            <Route path="/admin/production-rate-cards" element={<ProtectedRoute><AdminProductionRateCards /></ProtectedRoute>} />
            <Route path="/admin/sso" element={<AdminRoute><AdminSSO /></AdminRoute>} />
            <Route path="/admin/dealer-agreement" element={<AdminRoute><AdminDealerForms /></AdminRoute>} />
            <Route path="/admin/dealer-forms" element={<ProtectedRoute><AdminDealerForms /></ProtectedRoute>} />
            <Route path="/admin/dealer-forms/:formId" element={<ProtectedRoute><AdminDealerAgreement /></ProtectedRoute>} />
            <Route path="/dealer-forms/:formId" element={<ProtectedRoute><AdminDealerAgreement /></ProtectedRoute>} />
            <Route path="/onboarding" element={<ProtectedRoute><OnboardingBoard /></ProtectedRoute>} />
            <Route path="/tasks" element={<ProtectedRoute><Tasks /></ProtectedRoute>} />
            <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
            <Route path="/onboarding/:id" element={<ProtectedRoute><OnboardingDetail /></ProtectedRoute>} />
            <Route path="/onboarding/:id/program-setup" element={<ProtectedRoute><OnboardingProgramSetup /></ProtectedRoute>} />
            <Route path="/admin/products" element={<AdminRoute><AdminProducts /></AdminRoute>} />
            <Route path="/admin/onboarding-audit" element={<AdminRoute><OnboardingAudit /></AdminRoute>} />
            <Route path="/admin/agents" element={<AdminRoute><AdminAgents /></AdminRoute>} />
            <Route path="/admin/agents/:id" element={<AdminRoute><AdminAgentDetail /></AdminRoute>} />
            <Route path="/admin/onboarding/priority" element={<ProtectedRoute><AdminOnboardingPriority /></ProtectedRoute>} />
            <Route path="/agent/sign/:token" element={<AgentSignPage />} />
            <Route path="/agent/welcome/:token" element={<AgentWelcomePage />} />
            <Route path="/agent/apply" element={<AgentApply />} />
            <Route path="/dealer/apply" element={<DealerApply />} />
            <Route path="/onboarding/mine" element={<ProtectedRoute><AgentOnboardings /></ProtectedRoute>} />
            <Route path="/share/:token" element={<ProposalView shareMode={true} />} />
            <Route path="/rate/:token" element={<PublicRateCard />} />
            <Route path="*" element={<RootRedirect />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
